/**
 * Case Research's topics for the open case: the suggested questions (with
 * whether its files call for each), its key facts, its reputation pass and
 * its research memos, read (never written) and combined by
 * `buildResearchTopics`. Refetched when Casey's tools invalidate `research`,
 * `key_facts` or the files, and when the case's files change.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useCallback, useMemo } from "react";

import axiosInstance from "@/api/axiosConfig";
import type { Draft } from "@/api/types/draft";
import type { FsNode } from "@/features/caseFiles/types";
import { useKeyFacts } from "@/features/keyFacts/hooks/useKeyFacts";
import { queryClient } from "@/lib/query/queryClient";

import { useOnCaseInvalidate } from "../../context/caseInvalidation";
import {
  buildResearchTopics,
  type FitBy,
  type ResearchTopicsState,
  type StandingState,
  type StandingStatus,
  type SuggestedQuestion
} from "../researchTopics";

const statusOf = (error: unknown): number | null =>
  isAxiosError(error) ? error.response?.status ?? null : null;

type Questions = { questions: SuggestedQuestion[]; fitBy: FitBy | null };

/**
 * The questions with whether the case's files call for each, or
 * `unavailable` when the firm's plan has no Case Research (403).
 */
const fetchQuestions = async (
  individualId: string
): Promise<Questions | "unavailable"> => {
  try {
    const { data } = await axiosInstance.get<{
      questions?: SuggestedQuestion[];
      fit?: { by?: FitBy } | null;
    }>(`/research/${encodeURIComponent(individualId)}/suggested-questions`, {
      params: { fit: 1 }
    });
    return { questions: data.questions ?? [], fitBy: data.fit?.by ?? null };
  } catch (error) {
    if (statusOf(error) === 403) return "unavailable";
    throw error;
  }
};

/** The reputation pass; `off` while the environment doesn't have it (404). */
const fetchStanding = async (individualId: string): Promise<StandingState> => {
  try {
    const { data } = await axiosInstance.get<{ status?: StandingStatus }>(
      `/research/${encodeURIComponent(individualId)}/standing`
    );
    return { status: data.status ?? "none" };
  } catch (error) {
    const code = statusOf(error);
    if (code === 404 || code === 403) return { status: "off" };
    return { status: "none" };
  }
};

export const researchQueryKey = (individualId: string, part: string) =>
  ["case-research", part, individualId] as const;

/**
 * What Casey's picks read from the tree: each file's id, title, folder and
 * role. A change refetches; the server answers from its cache when the
 * categories it reads did not move.
 */
export const filesSignature = (nodes: readonly FsNode[]): string => {
  const rows = nodes
    .filter((n) => n.kind === "file")
    .map(
      (n) =>
        `${n.id}\u0001${n.title ?? n.name}\u0001${n.parent_id ?? ""}\u0001${
          n.document_role ?? ""
        }`
    )
    .sort()
    .join("\u0002");
  let hash = 5381;
  for (let i = 0; i < rows.length; i += 1)
    hash = (hash * 33 + rows.charCodeAt(i)) % 2147483647;
  return `${nodes.length}:${hash.toString(36)}`;
};

const NO_NODES: readonly FsNode[] = [];

export const useResearchTopics = (
  individualId: string,
  drafts: Draft[],
  nodes: readonly FsNode[] = NO_NODES
): ResearchTopicsState => {
  const facts = useKeyFacts(individualId || null);
  const files = useMemo(() => filesSignature(nodes), [nodes]);
  const questions = useQuery(
    {
      queryKey: [...researchQueryKey(individualId, "questions"), files],
      queryFn: () => fetchQuestions(individualId),
      enabled: !!individualId,
      staleTime: 5 * 60_000,
      placeholderData: keepPreviousData
    },
    queryClient
  );
  const refetchQuestions = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: researchQueryKey(individualId, "questions")
    });
  }, [individualId]);
  useOnCaseInvalidate(individualId, ["fs", "autocategorize"], refetchQuestions);
  const standing = useQuery(
    {
      queryKey: researchQueryKey(individualId, "standing"),
      queryFn: () => fetchStanding(individualId),
      enabled: !!individualId
    },
    queryClient
  );
  const refetchStanding = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: researchQueryKey(individualId, "standing")
    });
  }, [individualId]);
  useOnCaseInvalidate(individualId, ["research", "drafts"], refetchStanding);

  return useMemo<ResearchTopicsState>(() => {
    if (questions.data === "unavailable") return { kind: "unavailable" };
    if (questions.isError) return { kind: "error" };
    if (!questions.data || facts.loading || !standing.data)
      return { kind: "loading" };
    return {
      kind: "ready",
      ...buildResearchTopics({
        questions: questions.data.questions,
        fitBy: questions.data.fitBy,
        facts: facts.data,
        standing: standing.data,
        drafts,
        nodes
      })
    };
  }, [
    questions.data,
    questions.isError,
    facts.loading,
    facts.data,
    standing.data,
    drafts,
    nodes
  ]);
};
